import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, Download, Upload, Users, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/admin/DateInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageUpload from "@/components/admin/ImageUpload";
import api, { apiError } from "@/lib/api";

const EMPTY_FAMILY = {
  head_of_family: "", church_register_no: "", head_dob: "", address: "", phone: "",
  email: "", date_joined: "", baptised: "", communicant: "", notes: "", photo_url: "",
  is_public: false, photo_consent: false, members: [],
};

const familyIdPreview = (head, reg) => {
  const first = (head || "").trim().split(/\s+/)[0] || "";
  const num = String(reg || "").trim();
  if (!first || !/^\d+$/.test(num)) return "";
  const prefix = first.replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase().padEnd(3, "X");
  return `${prefix}${String(parseInt(num, 10)).padStart(4, "0")}`;
};

const EMPTY_MEMBER_CARD = { full_name: "", relationship: "", dob: "", baptised: "", communicant: "" };

const FAMILY_RELATIONSHIPS = [
  "Wife / Spouse", "Husband / Spouse", "Mother", "Father", "Grandmother", "Grandfather",
  "Sister", "Brother", "Daughter", "Son", "Daughter-in-law", "Son-in-law",
  "Granddaughter", "Grandson", "Other",
];

export default function FamilyDirectory() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [visibility, setVisibility] = useState("all");
  const [sort, setSort] = useState("head_of_family");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FAMILY);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const pageSize = 10;

  const load = useCallback(() => {
    const params = { page, page_size: pageSize, sort };
    if (q) params.q = q;
    if (visibility !== "all") params.is_public = visibility;
    api.get("/admin/families", { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [q, visibility, sort, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => { setForm(EMPTY_FAMILY); setEditing("new"); };
  const openEdit = (f) => {
    setForm({
      head_of_family: f.head_of_family || "", church_register_no: f.church_register_no || "",
      head_dob: f.head_dob || "", address: f.address || "", phone: f.phone || "", email: f.email || "",
      date_joined: f.date_joined || "", baptised: f.baptised || "", communicant: f.communicant || "",
      notes: f.notes || "", photo_url: f.photo_url || "",
      is_public: !!f.is_public, photo_consent: !!f.photo_consent, members: [],
    });
    setEditing(f);
  };

  const updateMember = (i, patch) => {
    setForm((prev) => ({ ...prev, members: prev.members.map((m, j) => (j === i ? { ...m, ...patch } : m)) }));
  };

  const save = async () => {
    if (!form.head_of_family.trim()) {
      toast.error("Head of Family is required");
      return;
    }
    if (editing === "new" && !/^\d+$/.test(form.church_register_no.trim())) {
      toast.error("Church Register No. is required (numbers only)");
      return;
    }
    if (!form.phone.trim()) {
      toast.error("Phone Number is required");
      return;
    }
    if (!form.baptised || !form.communicant) {
      toast.error("Baptised and Communicant Member are required (Yes / No)");
      return;
    }
    if (editing === "new") {
      for (const m of form.members) {
        if (!m.full_name.trim() || !m.relationship || !m.baptised || !m.communicant) {
          toast.error("Each family member needs Name, Relationship, Baptised and Communicant Member");
          return;
        }
      }
    }
    setSaving(true);
    try {
      if (editing === "new") {
        const res = await api.post("/admin/families", form);
        toast.success(`Family ${res.data.family_id} added`);
      } else {
        const payload = { ...form };
        if (!payload.church_register_no.trim()) delete payload.church_register_no;
        await api.put(`/admin/families/${editing.family_id}`, payload);
        toast.success("Family updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/families/${deleting.family_id}`);
      toast.success("Family deleted");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/families/export/csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "church_family_directory.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Directory exported");
    } catch (err) {
      toast.error(apiError(err, "Export failed"));
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const idPreview = familyIdPreview(form.head_of_family, form.church_register_no);
  const idWillChange = editing !== "new" && !!editing && !!idPreview
    && form.church_register_no.trim() !== String(editing?.church_register_no || "")
    && idPreview !== editing?.family_id;

  return (
    <div data-testid="family-directory" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy">Family Directory</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage church families and their members. All records are private by default.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="family-export-btn">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="family-import-btn">
            <Upload className="mr-2 h-4 w-4" /> Import Excel/CSV
          </Button>
          <Button onClick={openCreate} className="bg-navy text-cream hover:bg-navy-light" data-testid="family-add-btn">
            <Plus className="mr-2 h-4 w-4" /> Add Family
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-64 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search by head of family, family ID, phone or member name…"
            className="pl-9 bg-white"
            data-testid="family-search"
          />
        </div>
        <Select value={visibility} onValueChange={(v) => { setVisibility(v); setPage(1); }}>
          <SelectTrigger className="w-44 bg-white" data-testid="family-visibility-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All families</SelectItem>
            <SelectItem value="true">Public on website</SelectItem>
            <SelectItem value="false">Private</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44 bg-white" data-testid="family-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="head_of_family">Sort: Head A–Z</SelectItem>
            <SelectItem value="-head_of_family">Sort: Head Z–A</SelectItem>
            <SelectItem value="family_id">Sort: Family ID A–Z</SelectItem>
            <SelectItem value="-family_id">Sort: Family ID Z–A</SelectItem>
            <SelectItem value="-created_at">Sort: Newest first</SelectItem>
            <SelectItem value="created_at">Sort: Oldest first</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!data && <p className="text-sm text-muted-foreground">Loading…</p>}
      {data && data.items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-white/60 px-8 py-14 text-center" data-testid="family-empty">
          <Users className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 font-heading text-lg font-bold text-navy">No families found</p>
          <p className="mt-1 text-sm text-muted-foreground">Add a family manually or import your Excel/CSV directory.</p>
        </div>
      )}
      {data && data.items.length > 0 && (
        <>
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-white shadow-sm md:block">
            <table className="w-full text-sm" data-testid="family-table">
              <thead>
                <tr className="border-b border-border bg-secondary/60 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Family ID</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Head of Family</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Members</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Phone</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Visibility</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-navy/70">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((f) => (
                  <tr key={f.family_id} className="hover:bg-secondary/30" data-testid={`family-row-${f.family_id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-gold-dark">{f.family_id}</td>
                    <td className="max-w-56 truncate px-4 py-3 font-medium text-navy">
                      <Link to={`/admin/families/${f.family_id}`} className="hover:text-gold-dark hover:underline" data-testid={`family-row-open-${f.family_id}`}>
                        {f.head_of_family || "—"}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-foreground/80">{f.member_count}</td>
                    <td className="px-4 py-3 text-foreground/80">{f.phone || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${f.is_public ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>
                        {f.is_public ? "Public" : "Private"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" asChild data-testid={`family-view-${f.family_id}`} aria-label="View family">
                          <Link to={`/admin/families/${f.family_id}`}><Eye className="h-4 w-4 text-navy/60" /></Link>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(f)} data-testid={`family-edit-${f.family_id}`} aria-label="Edit family">
                          <Pencil className="h-4 w-4 text-navy/60" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleting(f)} data-testid={`family-delete-${f.family_id}`} aria-label="Delete family">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden" data-testid="family-cards">
            {data.items.map((f) => (
              <div key={f.family_id} className="rounded-lg border border-border bg-white p-5 shadow-sm" data-testid={`family-card-${f.family_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-xs font-semibold text-gold-dark">{f.family_id}</p>
                    <p className="mt-1 font-heading text-lg font-bold text-navy">
                      <Link to={`/admin/families/${f.family_id}`} className="hover:text-gold-dark" data-testid={`family-card-open-${f.family_id}`}>
                        {f.head_of_family || "—"}
                      </Link>
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {f.member_count} member{f.member_count === 1 ? "" : "s"} · {f.is_public ? "Public" : "Private"}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" asChild data-testid={`family-view-mobile-${f.family_id}`}>
                    <Link to={`/admin/families/${f.family_id}`}>View & Members</Link>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(f)} data-testid={`family-edit-mobile-${f.family_id}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => setDeleting(f)} className="text-destructive" data-testid={`family-delete-mobile-${f.family_id}`}>Delete</Button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between" data-testid="family-pagination">
            <p className="text-xs text-muted-foreground">
              Page {data.page} of {totalPages} · {data.total} famil{data.total === 1 ? "y" : "ies"}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="family-prev-page">Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="family-next-page">Next</Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="family-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy">{editing === "new" ? "Add Family" : `Edit Family ${editing?.family_id || ""}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="ff-head">Head of Family <span className="text-destructive">*</span></Label>
              <Input id="ff-head" data-testid="family-field-head" value={form.head_of_family} onChange={(e) => setForm({ ...form, head_of_family: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ff-regno">Church Register No. {editing === "new" && <span className="text-destructive">*</span>}</Label>
              <Input id="ff-regno" data-testid="family-field-register-no" inputMode="numeric" placeholder="e.g. 15" value={form.church_register_no} onChange={(e) => setForm({ ...form, church_register_no: e.target.value })} className="mt-1.5" />
              {editing === "new" ? (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="family-id-preview">
                  {idPreview
                    ? <>Family ID will be: <span className="font-mono font-semibold text-gold-dark">{idPreview}</span></>
                    : "Used only to generate the Family ID — e.g. Peter Bhatti + 15 → PET0015."}
                </p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="family-id-preview">
                  Family ID: <span className="font-mono font-semibold text-gold-dark">{editing?.family_id}</span>
                  {idWillChange
                    ? <> — will become <span className="font-mono font-semibold text-gold-dark">{idPreview}</span></>
                    : " — leave Register No. blank to keep this ID"}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="ff-phone">Phone Number <span className="text-destructive">*</span></Label>
              <Input id="ff-phone" data-testid="family-field-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ff-head-dob">Head of Family DOB</Label>
              <DateInput id="ff-head-dob" data-testid="family-field-head-dob" value={form.head_dob} onChange={(v) => setForm({ ...form, head_dob: v })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ff-address">Address</Label>
              <Textarea id="ff-address" data-testid="family-field-address" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Baptised <span className="text-destructive">*</span></Label>
                <Select value={form.baptised || undefined} onValueChange={(v) => setForm({ ...form, baptised: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="family-field-baptised"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Communicant Member <span className="text-destructive">*</span></Label>
                <Select value={form.communicant || undefined} onValueChange={(v) => setForm({ ...form, communicant: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="family-field-communicant"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="ff-joined">Date Joined</Label>
              <DateInput id="ff-joined" data-testid="family-field-joined" value={form.date_joined} onChange={(v) => setForm({ ...form, date_joined: v })} className="mt-1.5" />
            </div>
            <div>
              <Label>Family Photograph</Label>
              <div className="mt-1.5">
                <ImageUpload value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} testId="family-field-photo" />
              </div>
            </div>
            <div>
              <Label htmlFor="ff-notes">Notes (Private)</Label>
              <Textarea id="ff-notes" data-testid="family-field-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
              <p className="mt-1 text-xs text-muted-foreground">Never shown on the public website.</p>
            </div>
            <div className="rounded-md border border-gold/40 bg-gold/10 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox id="ff-public" data-testid="family-field-public" checked={form.is_public} onCheckedChange={(v) => setForm({ ...form, is_public: !!v })} />
                <Label htmlFor="ff-public" className="text-sm font-normal">Show this family on the public website</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="ff-consent" data-testid="family-field-consent" checked={form.photo_consent} onCheckedChange={(v) => setForm({ ...form, photo_consent: !!v })} />
                <Label htmlFor="ff-consent" className="text-sm font-normal">Family has consented to showing their photograph publicly</Label>
              </div>
            </div>
            {editing === "new" && (
              <div className="border-t border-border pt-4" data-testid="family-members-section">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-navy">Family Members</span>
                  <Button type="button" variant="outline" size="sm" data-testid="family-add-member-btn"
                    onClick={() => setForm({ ...form, members: [...form.members, { ...EMPTY_MEMBER_CARD, _k: crypto.randomUUID() }] })}>
                    <Plus className="mr-2 h-4 w-4" /> Add Family Member
                  </Button>
                </div>
                {form.members.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="family-members-empty">
                    No additional members yet — the Head of Family above is recorded automatically.
                  </p>
                )}
                <div className="space-y-3">
                  {form.members.map((m, i) => (
                    <div key={m._k ?? i} className="space-y-3 rounded-md border border-border bg-secondary/30 p-3" data-testid={`family-member-card-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-navy">Member {i + 1}</span>
                        <Button type="button" variant="ghost" size="sm" data-testid={`family-member-remove-${i}`}
                          onClick={() => setForm({ ...form, members: form.members.filter((_, j) => j !== i) })}>
                          <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove Member
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Member Name <span className="text-destructive">*</span></Label>
                          <Input className="mt-1" data-testid={`family-member-name-${i}`} value={m.full_name}
                            onChange={(e) => updateMember(i, { full_name: e.target.value })} />
                        </div>
                        <div>
                          <Label>Relationship to Head of Family <span className="text-destructive">*</span></Label>
                          <Select value={m.relationship || undefined} onValueChange={(v) => updateMember(i, { relationship: v })}>
                            <SelectTrigger className="mt-1" data-testid={`family-member-relationship-${i}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                            <SelectContent>
                              {FAMILY_RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Date of Birth</Label>
                          <DateInput className="mt-1" data-testid={`family-member-dob-${i}`} value={m.dob}
                            onChange={(v) => updateMember(i, { dob: v })} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Baptised <span className="text-destructive">*</span></Label>
                            <Select value={m.baptised || undefined} onValueChange={(v) => updateMember(i, { baptised: v })}>
                              <SelectTrigger className="mt-1" data-testid={`family-member-baptised-${i}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                              <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Communicant Member <span className="text-destructive">*</span></Label>
                            <Select value={m.communicant || undefined} onValueChange={(v) => updateMember(i, { communicant: v })}>
                              <SelectTrigger className="mt-1" data-testid={`family-member-communicant-${i}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                              <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="family-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-navy text-cream hover:bg-navy-light" data-testid="family-form-save">
              {saving ? "Saving…" : "Save Family"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent data-testid="family-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete family {deleting?.family_id}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove “{deleting?.head_of_family}” ({deleting?.family_id}) and all {deleting?.member_count ?? ""} of its member records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="family-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="family-delete-confirm">
              Delete Family
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />
    </div>
  );
}

const IMPORT_FIELDS = [
  ["family_id", "Family ID"], ["head_of_family", "Head of Family"],
  ["member_name", "Member Name"], ["relationship", "Relationship"], ["gender", "Gender"],
  ["dob", "Date of Birth"], ["phone", "Phone"], ["email", "Email"], ["address", "Address"],
  ["ministry", "Ministry"], ["date_joined", "Date Joined"], ["notes", "Notes"],
  ["is_public", "Public (Yes/No)"],
];

function ImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const rowStatus = (r) => {
    if (r.errors.length > 0) {
      return <span className="text-destructive">{r.errors.join("; ")}</span>;
    }
    if (r.warnings.length > 0) {
      return <span className="text-amber-700">{r.warnings.join("; ")}</span>;
    }
    return <span className="text-green-700">Ready</span>;
  };

  const reset = () => { setFile(null); setPreview(null); setMapping({}); setResult(null); };

  const doPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/families/import/preview", fd);
      setPreview(res.data);
      setMapping(res.data.mapping);
    } catch (err) {
      toast.error(apiError(err, "Could not read the file"));
    } finally {
      setLoading(false);
    }
  };

  const doCommit = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      const res = await api.post("/admin/families/import/commit", fd);
      setResult(res.data);
      toast.success("Import completed");
    } catch (err) {
      toast.error(apiError(err, "Import failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">Import Family Directory (Excel / CSV)</DialogTitle>
        </DialogHeader>

        {!preview && !result && (
          <div className="space-y-5 py-2">
            <p className="text-sm text-muted-foreground">
              Upload your Excel (.xlsx) or CSV file with columns such as Family ID, Member Name,
              Relationship, Gender, Date of Birth, Phone, Email, Address, Ministry, Date Joined, Notes.
              Rows sharing the same Family ID become members of one family.
            </p>
            <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="import-cancel">Cancel</Button>
              <Button onClick={doPreview} disabled={!file || loading} className="bg-navy text-cream" data-testid="import-preview-btn">
                {loading ? "Reading…" : "Preview Import"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {preview && !result && (
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="import-summary">
              {[
                ["Rows", preview.summary.total_rows],
                ["Valid rows", preview.summary.valid],
                ["Rows with errors", preview.summary.with_errors],
                ["Families in file", preview.summary.families_in_file],
              ].map(([label, val]) => (
                <div key={label} className="rounded-md border border-border bg-secondary/50 p-3 text-center">
                  <p className="font-heading text-xl font-bold text-navy">{val}</p>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold text-navy">Column mapping</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {preview.columns.map((col) => (
                  <div key={col} className="flex items-center gap-2">
                    <span className="w-32 truncate text-xs text-muted-foreground" title={col}>{col}</span>
                    <Select value={mapping[col] || "ignore"} onValueChange={(v) => setMapping({ ...mapping, [col]: v === "ignore" ? "" : v })}>
                      <SelectTrigger className="h-8 flex-1 text-xs" data-testid={`import-map-${col}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ignore">— Ignore —</SelectItem>
                        {IMPORT_FIELDS.map(([val, label]) => (
                          <SelectItem key={val} value={val}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto rounded-md border border-border" data-testid="import-preview-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-secondary/60 text-left">
                    <th className="px-3 py-2">Row</th>
                    <th className="px-3 py-2">Family ID</th>
                    <th className="px-3 py-2">Member Name</th>
                    <th className="px-3 py-2">Relationship</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {preview.rows.map((r) => (
                    <tr key={r.index} className={r.errors.length ? "bg-red-50" : ""}>
                      <td className="px-3 py-2">{r.index + 2}</td>
                      <td className="px-3 py-2 font-mono">{r.data.family_id || "—"}</td>
                      <td className="px-3 py-2">{r.data.member_name || "—"}</td>
                      <td className="px-3 py-2">{r.data.relationship || "—"}</td>
                      <td className="px-3 py-2">
                        {rowStatus(r)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setPreview(null); }} data-testid="import-back">Back</Button>
              <Button onClick={doCommit} disabled={loading || preview.summary.valid === 0} className="bg-navy text-cream" data-testid="import-confirm-btn">
                {loading ? "Importing…" : `Confirm Import (${preview.summary.valid} rows)`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-5 py-2" data-testid="import-result">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Families created", result.families_created],
                ["Families updated", result.families_updated],
                ["Members created", result.members_created],
                ["Members updated", result.members_updated],
              ].map(([label, val]) => (
                <div key={label} className="rounded-md border border-border bg-green-50 p-3 text-center">
                  <p className="font-heading text-xl font-bold text-green-800">{val}</p>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>
            {result.failed.length > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-semibold text-destructive">{result.failed.length} row(s) could not be imported:</p>
                <ul className="mt-2 space-y-1 text-xs text-destructive/90">
                  {result.failed.slice(0, 20).map((f) => (
                    <li key={f.row}>Row {f.row}: {f.error}</li>
                  ))}
                </ul>
              </div>
            )}
            <DialogFooter>
              <Button onClick={onDone} className="bg-navy text-cream" data-testid="import-done-btn">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
